import { useState, useCallback } from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
	getAuth,
	GoogleAuthProvider,
	signInWithPopup,
	signInWithCredential,
	signInWithEmailAndPassword,
	linkWithCredential,
	getAdditionalUserInfo,
	signOut,
	AuthCredential,
	UserCredential
} from "firebase/auth";

import { auth } from "../config/firebase/firebase";
import { environment } from "../config/environment/environment";
import { useCreateUserMutation } from "../graphql/mutations/create-user.generated";
import { useGetUserBasicByIdLazyQuery } from "../graphql/queries/get-user-basic-by-id.generated";
import { useAuth } from "context/AuthContext";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import routeNames from "constants/route-names";
import { GoogleAuthStatus } from "constants/google-auth-status";

// `AuthContext` watches the PRIMARY `auth` above globally: the instant it sees any signed-in
// Firebase user, it treats them as fully logged in and every screen built on `LoginLayout`
// immediately queries their Maestro profile. A brand new Google account has no `users` row
// yet, and that profile query fails outright under the `anonymous` role Hasura falls back to
// for an unrecognized uid — which unmounts whatever this hook was showing (the panel asking
// for the missing piece) before the person can even see it.
//
// Running the popup — and, for the linking flow, the password re-auth — against this isolated
// second Firebase app sidesteps all of that: nothing on the primary `auth` changes until
// `finishSignIn` explicitly promotes the sign-in there via `signInWithCredential`, once the
// `users` row is confirmed to exist. Same project, same config, just a separate local session.
const PREVIEW_APP_NAME = "google-auth-preview";
// Hot Module Replacement can re-run this module's top-level code without a full page reload,
// and initializeApp throws if an app with this name is already registered — hence the guard.
const previewApp = getApps().some(app => app.name === PREVIEW_APP_NAME)
	? getApp(PREVIEW_APP_NAME)
	: initializeApp(environment.firebaseConfig, PREVIEW_APP_NAME);
const previewAuth = getAuth(previewApp);

interface UseGoogleAuthProps {
	// Whether the Terms checkbox is already accepted on the surrounding form (sign-up screens
	// have their own checkbox; sign-in has none, so a fresh Google account always lands on
	// "pendingProfile" there until it is accepted through this hook's own panel).
	termsAccepted?: boolean;
	// Email an invitation link was addressed to (?email= on /signup). A Google account signing
	// in with any other address would silently miss the invitation's organization and role.
	invitedEmail?: string | null;
	// Where to go once authenticated, if there is nowhere more specific (a handover invite).
	redirectTo?: string;
	// The sign-up modal for Guides lives outside AuthLayout, so the "authNavigation" event it
	// listens for has no one to catch it there; this closes the modal instead of navigating.
	onAuthenticated?: () => void;
}

// Deletes a Firebase user we just created but are not going to use — an invitation-email
// mismatch, or the person cancelling out of "finish creating your account". Only ever called
// with `isNewUser: true`: a Google identity that already had a Firebase account before this
// attempt is never ours to delete, however this attempt turned out.
const discardUnusedFirebaseUser = async (user: any, isNewUser?: boolean) => {
	if (!isNewUser) {
		await signOut(previewAuth).catch(() => {});
		return;
	}
	try {
		await user.delete();
	} catch (deleteError) {
		console.log("Firebase user delete error: ", deleteError);
	}
};

const googleAuthErrorMessage = (code?: string): string | null => {
	switch (code) {
		case "auth/popup-closed-by-user":
		case "auth/cancelled-popup-request":
			// The person changed their mind; nothing went wrong.
			return null;
		case "auth/popup-blocked":
			return "Your browser blocked the Google sign-in window. Please allow pop-ups and try again.";
		case "auth/network-request-failed":
			return "We could not reach Google. Check your connection and try again.";
		case "auth/too-many-requests":
			return "Too many attempts. Please wait a moment and try again.";
		default:
			return "We could not sign you in with Google. Please try again, or contact support if the problem continues.";
	}
};

export const useGoogleAuth = ({ termsAccepted, invitedEmail, redirectTo, onAuthenticated }: UseGoogleAuthProps = {}) => {
	const { setLoggedUser, updateToken, logout } = useAuth();
	const { updateLoggedUser } = useLoggedUser();

	const [status, setStatus] = useState<GoogleAuthStatus>(GoogleAuthStatus.IDLE);
	const [error, setError] = useState<string | null>(null);
	// The preview-instance Firebase user: only ever used to read its email/uid/displayName
	// while a panel is open. It is never the user object we hand to the rest of the app.
	const [previewUser, setPreviewUser] = useState<any>(null);
	const [pendingProfile, setPendingProfile] = useState<{
		firstName: string;
		lastName: string;
		needsFirstName: boolean;
		needsLastName: boolean;
		// Whether Terms still need to be accepted through this hook's own panel: true on
		// screens with no checkbox of their own (sign-in), or when the surrounding form's
		// checkbox is there but unchecked.
		needsTerms: boolean;
		// Carried along so completeAccountCreation can hand it to finishSignIn — it can only
		// come from the original popup/link result, never reconstructed afterwards.
		credential: AuthCredential;
		// Whether Firebase just created this account for this attempt — the only case where
		// cancelling is safe to clean up with a real delete instead of a plain sign-out.
		isNewUser?: boolean;
	} | null>(null);
	const [pendingLink, setPendingLink] = useState<{ email: string; credential: AuthCredential } | null>(null);

	// A fresh Google user has no `users` row yet, so this runs under the `anonymous` role (the
	// auth webhook falls back to it when it cannot resolve session variables for the uid) —
	// hence the separate, narrower query: the full get-user-by-id one reads columns and
	// relationships `anonymous` has no permission for. It needs no auth token either way.
	const [getUserById] = useGetUserBasicByIdLazyQuery({ fetchPolicy: "network-only" });

	// The only place that ever touches the primary, globally-watched `auth`. Everything before
	// this point — the popup, the password re-auth for linking — runs on `previewAuth` instead.
	const finishSignIn = useCallback(
		async (credential: AuthCredential) => {
			const { user } = await signInWithCredential(auth, credential);
			setLoggedUser?.(user);
			await updateToken?.(user);
			signOut(previewAuth).catch(() => {});

			if (onAuthenticated) {
				onAuthenticated();
				return;
			}

			window.dispatchEvent(
				new CustomEvent("authNavigation", {
					detail: { route: redirectTo || routeNames.DASHBOARD }
				})
			);
		},
		[setLoggedUser, updateToken, onAuthenticated, redirectTo]
	);

	const handleOnError = useCallback(
		async (mutationError: any, user: any) => {
			console.log("createUserMutation error: ", mutationError);
			setError(
				"We could not create your account. Please try again, or contact support if the problem continues."
			);
			setStatus(GoogleAuthStatus.IDLE);

			// The Firebase account exists but the user row does not; remove it so the next
			// attempt does not fail on the email already being taken with no way out.
			if (logout) logout(false);
			try {
				await user?.delete();
			} catch (deleteError) {
				console.log("Firebase user delete error: ", deleteError);
			}
		},
		[logout]
	);

	const [createUserMutation] = useCreateUserMutation({ errorPolicy: "none" });

	// Once we know who the Google account belongs to (new or already existing), route past
	// this hook's own "pendingProfile" gate and either finish or create the `users` row.
	// `credential` is what `finishSignIn` replays against the primary `auth` — the same one
	// from the original popup (or, after linking, the same one made valid for that account).
	// `termsAlreadyAccepted` overrides the hook's own `termsAccepted` prop for the call that
	// follows the panel itself, where the panel's own checkbox (not the prop, which is stale
	// by then) is what settled it.
	const proceedWithUser = useCallback(
		async (
			user: any,
			credential: AuthCredential,
			additionalInfo: { firstName: string; lastName: string; email: string },
			termsAlreadyAccepted?: boolean,
			isNewUser?: boolean
		) => {
			const { data } = await getUserById({
				variables: { id: user.uid }
			});
			// A soft-deleted row still exists for this uid, but it should not let someone back
			// in — same rule the password flow applies via get-user-by-id's own filter.
			const existingUser = data?.users_by_pk?.find(row => !row.deleted_at);

			if (existingUser) {
				updateLoggedUser(existingUser);
				await finishSignIn(credential);
				return;
			}

			const { firstName, lastName } = additionalInfo;
			const needsFirstName = !firstName;
			const needsLastName = !lastName;
			const needsTerms = !(termsAlreadyAccepted ?? termsAccepted);

			if (needsFirstName || needsLastName || needsTerms) {
				// Missing what we need to create the row: ask for it instead of guessing —
				// Google's OIDC profile always carries a first name in practice, but nothing
				// enforces that, and a fabricated "Google" would create a wrong-looking account.
				setPreviewUser(user);
				setPendingProfile({ firstName, lastName, needsFirstName, needsLastName, needsTerms, credential, isNewUser });
				setStatus(GoogleAuthStatus.PENDING_PROFILE);
				return;
			}

			setStatus(GoogleAuthStatus.LOADING);
			try {
				const { data: createData } = await createUserMutation({
					variables: {
						id: user.uid,
						first_name: firstName,
						last_name: lastName,
						email: additionalInfo.email,
						phone_number: null
					}
				});

				if (!createData?.create_user?.success) {
					throw new Error(createData?.create_user?.error || "create_user did not succeed");
				}

				updateLoggedUser(createData.create_user.createdUser);
				await finishSignIn(credential);
			} catch (mutationError) {
				await handleOnError(mutationError, user);
			}
		},
		[getUserById, updateLoggedUser, finishSignIn, termsAccepted, createUserMutation, handleOnError]
	);

	const startGoogleAuth = useCallback(async () => {
		setError(null);
		setStatus(GoogleAuthStatus.LOADING);

		try {
			const provider = new GoogleAuthProvider();
			provider.setCustomParameters({ prompt: "select_account" });

			const result: UserCredential = await signInWithPopup(previewAuth, provider);
			const { user } = result;
			const credential = GoogleAuthProvider.credentialFromResult(result);

			if (!credential) {
				// Should not happen for a successful Google popup, but this is exactly what
				// finishSignIn needs to promote the sign-in later — nothing to recover without it.
				throw new Error("Google sign-in did not return a usable credential");
			}

			const additionalUserInfo = getAdditionalUserInfo(result);
			const isNewUser = additionalUserInfo?.isNewUser;

			if (invitedEmail && user.email?.toLowerCase() !== invitedEmail.toLowerCase()) {
				// Firebase already created this account the moment the popup succeeded — if this
				// attempt is the reason it exists, undo that instead of leaving it stranded with
				// no `users` row and no invitation pointing at it.
				await discardUnusedFirebaseUser(user, isNewUser);
				setError(`Please continue with the Google account for ${invitedEmail}.`);
				setStatus(GoogleAuthStatus.IDLE);
				return;
			}

			const profile = additionalUserInfo?.profile as Record<string, any> | undefined;
			const displayNameParts = (user.displayName || "").trim().split(/\s+/);

			const firstName = (profile?.given_name as string) || displayNameParts[0] || "";
			const lastName =
				(profile?.family_name as string) || (displayNameParts.length > 1 ? displayNameParts.slice(1).join(" ") : "");

			await proceedWithUser(user, credential, { firstName, lastName, email: user.email || "" }, undefined, isNewUser);
		} catch (authError: any) {
			console.log("Google sign-in error: ", authError);

			if (authError?.code === "auth/account-exists-with-different-credential") {
				const credential = GoogleAuthProvider.credentialFromError(authError);
				const conflictingEmail = authError.customData?.email;

				if (credential && conflictingEmail) {
					setPendingLink({ email: conflictingEmail, credential });
					setStatus(GoogleAuthStatus.PENDING_LINK);
					return;
				}
			}

			const message = googleAuthErrorMessage(authError?.code);
			setError(message);
			setStatus(GoogleAuthStatus.IDLE);
		}
	}, [invitedEmail, proceedWithUser]);

	// Every account in this app is created with a password first, so asking for it directly
	// is the whole flow — Firebase does not expose which providers exist once email
	// enumeration protection is on (fetchSignInMethodsForEmail returns an empty list).
	// Both the password check and the linking happen on `previewAuth`: the account already
	// exists, so signing in on the primary `auth` here would let AuthContext wave the person
	// through to the dashboard before the Google credential is actually linked.
	const completeLinkWithPassword = useCallback(
		async (password: string) => {
			if (!pendingLink) return;

			setError(null);
			setStatus(GoogleAuthStatus.LOADING);

			try {
				const existingCredential = await signInWithEmailAndPassword(previewAuth, pendingLink.email, password);
				const linkedResult = await linkWithCredential(existingCredential.user, pendingLink.credential);
				const { user } = linkedResult;
				const googleCredential = pendingLink.credential;

				setPendingLink(null);
				await proceedWithUser(user, googleCredential, {
					firstName: user.displayName?.split(" ")[0] || "",
					lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
					email: user.email || ""
				});
			} catch (linkError: any) {
				console.log("linkWithCredential error: ", linkError);
				setError(
					linkError?.code === "auth/wrong-password" || linkError?.code === "auth/invalid-credential"
						? "That password is incorrect."
						: "We could not connect your Google account. Please try again."
				);
				setStatus(GoogleAuthStatus.PENDING_LINK);
			}
		},
		[pendingLink, proceedWithUser]
	);

	// Completes account creation once the missing piece (last name and/or Terms acceptance)
	// has been filled in through the panel this hook drives.
	const completeAccountCreation = useCallback(
		async (firstName: string, lastName: string, termsAcceptedInPanel: boolean) => {
			if (!previewUser || !pendingProfile) return;

			await proceedWithUser(
				previewUser,
				pendingProfile.credential,
				{
					firstName: firstName || pendingProfile.firstName,
					lastName: lastName || pendingProfile.lastName,
					email: previewUser.email || ""
				},
				!pendingProfile.needsTerms || termsAcceptedInPanel
			);
			setPendingProfile(null);
			setPreviewUser(null);
		},
		[previewUser, pendingProfile, proceedWithUser]
	);

	const cancel = useCallback(async () => {
		if (previewUser) {
			// Same rule as the invitation-mismatch case: only delete what this attempt itself
			// created. Cancelling out of "finish creating your account" should not leave a
			// Firebase-only shell behind, but it must never touch a pre-existing account.
			await discardUnusedFirebaseUser(previewUser, pendingProfile?.isNewUser);
		}
		setPreviewUser(null);
		setPendingProfile(null);
		setPendingLink(null);
		setError(null);
		setStatus(GoogleAuthStatus.IDLE);
	}, [previewUser, pendingProfile]);

	return {
		status,
		error,
		pendingProfile,
		pendingLink,
		startGoogleAuth,
		completeAccountCreation,
		completeLinkWithPassword,
		cancel,
		loading: status === GoogleAuthStatus.LOADING
	};
};
